import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { analytics, db, Timestamp, logEvent } from '../firebase-config'; // Adjust path as needed
import {
  collection,
  addDoc,
} from "firebase/firestore";

export const useFeedbackForm = () => {
  const { t } = useTranslation('FeedbackPage');
  const [searchParams] = useSearchParams();

  // Form State
  const [feedbackText, setFeedbackText] = useState('');
  const [rating, setRating] = useState(5); // Default to 5 stars

  // Submission State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // User/Tracking Info
  const [userEmail, setUserEmail] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const browserInfo = {
    browser: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  // Effect to extract query parameters on mount
  useEffect(() => {
    setUserEmail(searchParams.get('email') || '');
    setUtmSource(searchParams.get('utm_source') || '');
    setUtmMedium(searchParams.get('utm_medium') || '');
    setUtmCampaign(searchParams.get('utm_campaign') || '');
  }, [searchParams]);
  //get browser info
  useEffect(() => {
    const browserInfo = {
      browser: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (analytics) {
      logEvent(analytics, 'feedback_page_view', { ...browserInfo });
    }
  }, []);

  // Log event on mount
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'feedback_page_view', {
        userEmail,
        utmSource,
        utmMedium,
        utmCampaign,
      });
    }
  }, [userEmail, utmSource, utmMedium, utmCampaign]);

  // Submission Handler (using useCallback to stabilize the function reference)
  const submitFeedback = useCallback(async () => {
    // Basic validation
    if (!feedbackText.trim()) {
      setError(t('feedback_text_required'));
      setSuccess(''); // Clear success message on new error
      return;
    }

    // Reset status and set loading
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const feedbackData = {
        text: feedbackText.trim(),
        rating: rating,
        createdAt: Timestamp.now(),
        // Only include if values exist
        ...(userEmail && { userEmail }),
        ...(utmSource && { utmSource }),
        ...(utmMedium && { utmMedium }),
        ...(utmCampaign && { utmCampaign }),
        ...browserInfo, // Add browser info
      };

      await addDoc(collection(db, 'feedbacks'), feedbackData);

      // Reset form and show success
      setFeedbackText('');
      setRating(5); // Reset rating to default
      setSuccess(t('feedback_success'));

      // Analytics
      if (analytics) {
        logEvent(analytics, 'feedback_submitted', { rating });
      }

    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(t('feedback_error'));
      // Consider logging the detailed error to a monitoring service
    } finally {
      // Always stop loading
      setIsLoading(false);
    }
  }, [feedbackText, rating, userEmail, utmSource, utmMedium, utmCampaign, t]); // Dependencies for useCallback

  return {
    // Form state and setters
    feedbackText,
    setFeedbackText,
    rating,
    setRating,
    // Submission state
    isLoading,
    error,
    success,
    // Submission handler
    submitFeedback,
    // User info (read-only from the hook's perspective)
    userEmail,
    utmSource,
    utmMedium,
    utmCampaign,
  };
};